import { createAsyncThunk, createAction } from '@reduxjs/toolkit';

import {
    MPMIntegration,
    Integration,
    UpdateLicenseRequestPayload,
    MPMLicense,
    License,
    CreateIntegrationRequestPayload,
} from 'lib/types';
import {
    createIntegration,
    createLicense,
    deleteIntegration,
    retrieveAllLicenses,
    retrieveIntegrations,
    retrieveLicense,
    retrieveLicenses,
    updateIntegration,
    updateLicense,
} from 'lib/api';

/// THUNKS

export const retrieveIntegrationsThunk = createAsyncThunk(
    'integrations/retrieveIntegrations',
    async (_, thunkAPI) => {
        try {
            return await retrieveIntegrations();
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

export const createIntegrationThunk = createAsyncThunk<
    MPMIntegration,
    CreateIntegrationRequestPayload
>(
    'integrations/createIntegration',
    async (integration: MPMIntegration, thunkAPI) => {
        try {
            return await createIntegration(integration);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

export const updateIntegrationThunk = createAsyncThunk<
    Omit<MPMIntegration, 'id' | 'accountId'>,
    { integration: MPMIntegration }
>(
    'integrations/updateIntegration',
    async ({ integration }, thunkAPI) => {
        try {
            return await updateIntegration(integration);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

export const deleteIntegrationThunk = createAsyncThunk<
    void,
    { integrationId: string }
>(
    'integrations/deleteIntegration',
    async ({ integrationId }, thunkAPI) => {
        try {
            return await deleteIntegration(integrationId);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

// integrationId parameter is used in the reducer for
// mapping the license to the correct integration
export const retrieveLicenseThunk = createAsyncThunk<
    MPMLicense,
    { integrationId: string; licenseId: string }
>(
    'integrations/retrieveLicense',
    async ({ integrationId, licenseId }, thunkAPI) => {
        try {
            return await retrieveLicense(licenseId);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

export const retrieveLicensesThunk = createAsyncThunk<
    MPMLicense[],
    { integrationId: string }
>(
    'integrations/retrieveLicenses',
    async ({ integrationId }, thunkAPI) => {
        try {
            return await retrieveLicenses(integrationId);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

export const retrieveAllLicensesThunk = createAsyncThunk<
    { integrationId: string; licenses: MPMLicense[] }[],
    { integrationIds: string[] }
>(
    'integrations/retrieveAllLicenses',
    async ({ integrationIds }, thunkAPI) => {
        try {
            return await retrieveAllLicenses(integrationIds);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

export const createLicenseThunk = createAsyncThunk<
    MPMLicense,
    {
        integrationId: string;
        license: License;
    }
>(
    'integrations/createLicense',
    async ({ integrationId, license }, thunkAPI) => {
        try {
            return await createLicense(integrationId, license);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

export const updateLicenseThunk = createAsyncThunk<
    Omit<
        MPMLicense,
        'id' | 'integrationId' | 'licenseType' | 'softwareLicenseCode'
    >,
    {
        integrationId: string;
        licenseId: string;
        licenseData: UpdateLicenseRequestPayload;
    }
>(
    // The integrationId property is used to allow it
    // to be used in the "fulfilled" state as action.meta.arg
    // to map the license to the correct integration
    'integrations/updateLicense',
    async ({ integrationId, licenseId, licenseData }, thunkAPI) => {
        try {
            return await updateLicense(licenseId, licenseData);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data.error);
        }
    }
);

/// ACTIONS

export const resetCreateIntegrationStatus = createAction(
    'integrations/resetCreateIntegrationStatus'
);

export const resetCreateOrUpdateLicenseStatus = createAction(
    'integrations/resetCreateOrUpdateLicenseStatus'
);

export const removeError = createAction('integrations/removeError');
